import {
  loadFixture, findById, nextId, parseBody, parseQuery, urlParam, CURRENT_USER_PROFILE_REF,
  groupIdentifierFromUrl, groupFixtureName,
} from '../mockDataStore';

const PAYMENTS = /^\/?api\/v1\/groups\/[\w-]+\/events\/(\d+)\/payments\/?$/;
const PAYMENT_DETAIL = /^\/?api\/v1\/groups\/[\w-]+\/events\/(\d+)\/payments\/(\d+)\/?$/;
const MY_PAYMENT = /^\/?api\/v1\/groups\/[\w-]+\/events\/(\d+)\/payments\/me\/?$/;
const EVENT_QUOTE = /^\/?api\/v1\/groups\/[\w-]+\/events\/(\d+)\/payments\/quote\/?(\?.*)?$/;
const CHECKOUT = /^\/?api\/v1\/groups\/[\w-]+\/events\/(\d+)\/payments\/checkout\/?$/;
const REFUND = /^\/?api\/v1\/groups\/[\w-]+\/events\/(\d+)\/payments\/(\d+)\/refund\/?$/;
const ACCOUNT = /^\/?api\/v1\/groups\/([\w-]+)\/payments\/account\/?$/;
const ACCOUNT_LINK = /^\/?api\/v1\/groups\/([\w-]+)\/payments\/account\/(onboarding|dashboard)-link\/?$/;
const PRICE_QUOTE = /^\/?api\/v1\/groups\/[\w-]+\/payments\/quote\/?(\?.*)?$/;
const EXPORT = /^\/?api\/v1\/groups\/[\w-]+\/payments\/export\/?$/;

// The API's default rates (PaymentSettings): Reunacy 3%, processing 3.4% + 30¢.
const RATES = { feePercent: 3, processingPercent: 3.4, processingFixedCents: 30 };
// Rows that count toward what's been paid (less any refunded part of the ticket).
const COUNTED_STATUSES = ['paid', 'partially_refunded'];

const MANUAL_METHODS = ['venmo', 'zelle', 'paypal', 'cash', 'check', 'other', 'comp'];
const RSVP_VALUES = { Attending: 'attending', 'Unable to Attend': 'not_attending' };

// Money is handled in whole cents, like the API's Decimal, so $25.10 × 3 is exactly $75.30.
const toCents = (value) => Math.round(Number(value) * 100);
const money = (value) => Number(value).toFixed(2);
const centsToMoney = (cents) => (cents / 100).toFixed(2);

/**
 * The API's derived payment status for one attendee (see the Phase 1a contract): only
 * `paid` ledger rows count, and the amount due is ticket price × (1 + guests) when going.
 */
/**
 * The API's fee math (payments.fees.quote): member pays ticket + Reunacy fee + processing,
 * total rounded up to the cent so the organizer always receives the full ticket amount.
 */
export function quoteCents(ticketCents) {
  const fee = Math.round((ticketCents * RATES.feePercent) / 100);
  const total = Math.ceil(
    (ticketCents + fee + RATES.processingFixedCents) / (1 - RATES.processingPercent / 100) - 1e-9,
  );
  return { ticket: ticketCents, fee, processing: total - ticketCents - fee, total };
}

function quoteFor(priceCents, quantity) {
  const all = quoteCents(priceCents * quantity);
  return {
    quantity,
    balance: false,
    ticket_amount: centsToMoney(all.ticket),
    service_fee: centsToMoney(all.fee + all.processing),
    total: centsToMoney(all.total),
    per_ticket_total: centsToMoney(quoteCents(priceCents).total),
  };
}

// Paying an outstanding balance that isn't whole tickets (payments.cards.balance_quote_payload).
function balanceQuoteFor(priceCents, balanceCents) {
  const all = quoteCents(balanceCents);
  return {
    quantity: null,
    balance: true,
    ticket_amount: centsToMoney(all.ticket),
    service_fee: centsToMoney(all.fee + all.processing),
    total: centsToMoney(all.total),
    per_ticket_total: centsToMoney(quoteCents(priceCents).total),
  };
}

// What the member's Pay button charges (payments.cards.my_card_quote): the whole balance when it
// isn't whole tickets (and is at least Stripe's 50¢ minimum), else the whole tickets owed.
function myCardQuote(priceCents, owedCents) {
  if (!priceCents || owedCents <= 0) return null;
  if (owedCents % priceCents) {
    const quote = balanceQuoteFor(priceCents, owedCents);
    return toCents(quote.total) >= 50 ? quote : null;
  }
  return quoteFor(priceCents, owedCents / priceCents);
}

// Refunds give back the ticket and Reunacy's fee (never processing), split in proportion.
const refundableBaseCents = (payment) => toCents(payment.amount) + toCents(payment.reunacy_fee_amount || 0);
function ticketShareCents(payment, refundedCents) {
  const base = refundableBaseCents(payment);
  return base ? Math.round((refundedCents * toCents(payment.amount)) / base) : 0;
}
const countedCents = (payment) => (
  toCents(payment.amount) - ticketShareCents(payment, toCents(payment.refunded_amount || 0))
);
const refundableCents = (payment) => (COUNTED_STATUSES.includes(payment.status)
  ? Math.max(refundableBaseCents(payment) - toCents(payment.refunded_amount || 0), 0) : 0);

function summarize({ rsvp, guests, ticketPrice, payments }) {
  const counted = payments.filter((payment) => COUNTED_STATUSES.includes(payment.status));
  const amountPaid = counted.reduce((total, payment) => total + countedCents(payment), 0);
  const attending = rsvp === 'attending';
  const amountDue = attending ? toCents(ticketPrice) * (1 + guests) : 0;
  let status;
  if (counted.some((payment) => payment.method === 'comp')) status = 'comped';
  else if ((amountDue > 0 && amountPaid >= amountDue) || (amountDue === 0 && amountPaid > 0)) status = 'paid';
  else if (amountPaid > 0) status = 'partial';
  else if (attending) status = 'unpaid';
  else status = rsvp === 'not_attending' ? 'not_attending' : 'no_response';
  const last = counted[counted.length - 1];
  return {
    rsvp,
    guests: attending ? guests : 0,
    quantity: attending ? 1 + guests : 0,
    amount_due: centsToMoney(amountDue),
    amount_paid: centsToMoney(amountPaid),
    status,
    last_method: last ? last.method : null,
    payments: payments.map((payment) => ({
      id: payment.id,
      amount: payment.amount,
      method: payment.method,
      status: payment.status,
      paid_at: payment.paid_at,
      note: payment.note,
      recorded_by_name: payment.recorded_by_name,
      ...(payment.method === 'card' ? {
        quantity: payment.quantity,
        reunacy_fee_amount: payment.reunacy_fee_amount,
        processing_amount: payment.processing_amount,
        total_charged: payment.total_charged,
        refunded_amount: payment.refunded_amount || '0.00',
        refundable_amount: centsToMoney(refundableCents(payment)),
      } : {}),
    })),
  };
}

async function loadEvent(url, eventId) {
  const events = await loadFixture(groupFixtureName('events/events', groupIdentifierFromUrl(url)));
  return findById(events.results, eventId);
}

async function eventPayments(url, eventId) {
  const groupId = groupIdentifierFromUrl(url);
  const event = await loadEvent(url, eventId);
  if (!event) return null;
  const [profiles, attendees, ledger] = await Promise.all([
    loadFixture(groupFixtureName('groups/profiles', groupId)),
    loadFixture(groupFixtureName('events/event-attendees', groupId)),
    loadFixture('events/event-payments'),
  ]);
  const ticketPrice = Number(event.ticket_price) || 0;
  const members = profiles.results
    .filter((profile) => !profile.status || profile.status === 'active')
    .map((profile) => {
      const attendee = attendees.results.find((item) => item.profile === profile.id) || {};
      return {
        profile_id: profile.id,
        profile_slug: profile.slug,
        first_name: profile.first_name,
        last_name: profile.last_name,
        ...summarize({
          rsvp: RSVP_VALUES[attendee.rsvp] || null,
          guests: attendee.guests_amount || 0,
          ticketPrice,
          payments: ledger.results.filter(
            (payment) => payment.event === eventId && payment.profile_id === profile.id,
          ),
        }),
      };
    });
  const count = (status) => members.filter((member) => member.status === status).length;
  const sum = (key) => members.reduce((total, member) => total + toCents(member[key]), 0);
  const due = sum('amount_due');
  const collected = sum('amount_paid');
  // Comped attendees keep their amount due but owe nothing.
  const outstanding = members
    .filter((member) => member.status !== 'comped')
    .reduce(
      (total, member) => total + Math.max(toCents(member.amount_due) - toCents(member.amount_paid), 0),
      0,
    );
  return {
    event: {
      id: event.id,
      name: event.name,
      start_datetime: event.start_datetime,
      ticket_price: event.ticket_price || null,
      has_price: ticketPrice > 0,
      accepts_card_payments: !!event.accepts_card_payments,
    },
    totals: {
      amount_due: centsToMoney(due),
      amount_collected: centsToMoney(collected),
      amount_outstanding: centsToMoney(outstanding),
      attending: members.filter((member) => member.rsvp === 'attending').length,
      paid: count('paid'),
      partial: count('partial'),
      unpaid: count('unpaid'),
      comped: count('comped'),
    },
    members,
    // The demo has no public RSVP fixtures.
    public_rsvps: [],
  };
}

async function myOwedCents(url, eventId) {
  const data = await eventPayments(url, eventId);
  if (!data) return null;
  const me = data.members.find((member) => member.profile_id === CURRENT_USER_PROFILE_REF.id);
  return me && me.status !== 'comped'
    ? Math.max(toCents(me.amount_due) - toCents(me.amount_paid), 0) : 0;
}

function validationErrors(body, { requirePayer }) {
  const errors = {};
  if (requirePayer && !body.profile_id === !body.public_rsvp_id) {
    errors.non_field_errors = ['Give either profile_id or public_rsvp_id.'];
  }
  if (body.method !== undefined && !MANUAL_METHODS.includes(body.method)) {
    errors.method = ['Choose a valid method.'];
  }
  if (body.amount !== undefined) {
    const amount = Number(body.amount);
    if (Number.isNaN(amount) || amount < 0 || (amount === 0 && body.method !== 'comp')) {
      errors.amount = ['Enter an amount above 0.'];
    }
  }
  if (body.note && body.note.length > 500) errors.note = ['Keep the note to 500 characters.'];
  return errors;
}

const EXPORT_HEADER = [
  'Date paid', 'Event', 'Event date', 'Payer name', 'Payer email', 'Member or guest', 'Method', 'Status',
  'Ticket amount', 'Quantity', 'Service fee charged', 'Total charged', 'Refunded', 'Reunacy fee', 'Note',
  'Recorded by',
];
const METHOD_LABELS = {
  card: 'Card', venmo: 'Venmo', zelle: 'Zelle', paypal: 'PayPal', cash: 'Cash', check: 'Check', other: 'Other', comp: 'Comp',
};
const STATUS_LABELS = {
  paid: 'Paid', partially_refunded: 'Partially refunded', refunded: 'Refunded', voided: 'Voided',
};
const csvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The group's ledger as the API's CSV export (payments.export): one row per payment, abandoned checkouts left out. */
async function paymentsCsv(url) {
  const groupId = groupIdentifierFromUrl(url);
  const [events, profiles, ledger] = await Promise.all([
    loadFixture(groupFixtureName('events/events', groupId)),
    loadFixture(groupFixtureName('groups/profiles', groupId)),
    loadFixture('events/event-payments'),
  ]);
  // The ledger fixture is shared by every demo group: only this group's events are exported.
  const rows = ledger.results
    .filter((payment) => STATUS_LABELS[payment.status] && findById(events.results, payment.event))
    .map((payment) => {
      const event = findById(events.results, payment.event);
      const profile = findById(profiles.results, payment.profile_id) || {};
      const card = payment.method === 'card';
      return [
        payment.paid_at, event.name, (event.start_datetime || '').slice(0, 10),
        `${profile.first_name || ''} ${profile.last_name || ''}`.trim(), profile.email || '', 'Member',
        METHOD_LABELS[payment.method] || payment.method, STATUS_LABELS[payment.status], payment.amount,
        card ? payment.quantity : '',
        card && payment.total_charged ? centsToMoney(toCents(payment.total_charged) - toCents(payment.amount)) : '',
        card ? payment.total_charged : '', card ? payment.refunded_amount || '0.00' : '',
        card ? payment.reunacy_fee_amount : '', payment.note, payment.recorded_by_name,
      ];
    });
  return [EXPORT_HEADER, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

export default function registerPaymentMocks(mock) {
  mock.onGet(MY_PAYMENT).reply(async (config) => {
    const data = await eventPayments(config.url, Number(urlParam(config, MY_PAYMENT)));
    if (!data) return [404, { detail: 'Not found.' }];
    const me = data.members.find((member) => member.profile_id === CURRENT_USER_PROFILE_REF.id);
    const accepts = data.event.has_price && data.event.accepts_card_payments;
    const priceCents = toCents(data.event.ticket_price || 0);
    const owedCents = me && me.status !== 'comped'
      ? Math.max(toCents(me.amount_due) - toCents(me.amount_paid), 0) : 0;
    return [200, {
      has_price: data.event.has_price,
      quantity: me ? me.quantity : 0,
      amount_due: me ? me.amount_due : '0.00',
      amount_paid: me ? me.amount_paid : '0.00',
      status: me ? me.status : 'no_response',
      accepts_card_payments: accepts,
      quote: accepts ? myCardQuote(priceCents, owedCents) : null,
    }];
  });

  mock.onGet(EVENT_QUOTE).reply(async (config) => {
    const event = await loadEvent(config.url, Number(urlParam(config, EVENT_QUOTE)));
    if (!event || !(Number(event.ticket_price) > 0) || !event.accepts_card_payments) {
      return [404, { detail: 'Not found.' }];
    }
    const query = { ...parseQuery(config.url), ...(config.params || {}) };
    const priceCents = toCents(event.ticket_price);
    if (String(query.balance) === 'true') {
      const owedCents = await myOwedCents(config.url, event.id);
      const quote = owedCents ? myCardQuote(priceCents, owedCents) : null;
      return quote && quote.balance ? [200, quote]
        : [400, { non_field_errors: ['What you owe has changed. Reload the page to see it.'] }];
    }
    const quantity = Math.max(parseInt(query.quantity, 10) || 1, 1);
    return [200, quoteFor(priceCents, quantity)];
  });

  mock.onGet(PRICE_QUOTE).reply((config) => {
    const query = { ...parseQuery(config.url), ...(config.params || {}) };
    const price = Number(query.ticket_price);
    if (!(price > 0)) return [400, { ticket_price: ['Enter a ticket price above 0.'] }];
    return [200, quoteFor(toCents(price), 1)];
  });

  // The demo has no Stripe account: payouts show as ready and links come back to the page.
  mock.onGet(ACCOUNT).reply(async () => [200, await loadFixture('groups/payout-account')]);

  mock.onPost(ACCOUNT_LINK).reply((config) => [200, {
    url: `/group/${urlParam(config, ACCOUNT_LINK)}/admin/event-payments?payouts=return`,
  }]);

  mock.onPost(CHECKOUT).reply(() => [400, { detail: 'Card payments aren\'t available in the demo.' }]);

  mock.onPost(REFUND).reply(async (config) => {
    const ledger = await loadFixture('events/event-payments');
    const payment = ledger.results.find((item) => (
      item.id === Number(urlParam(config, REFUND, 2)) &&
      item.event === Number(urlParam(config, REFUND))
    ));
    if (!payment) return [404, { detail: 'Not found.' }];
    if (payment.method !== 'card' || !COUNTED_STATUSES.includes(payment.status)) {
      return [400, { detail: 'Only card payments can be refunded.' }];
    }
    const left = refundableCents(payment);
    const body = parseBody(config);
    const amount = body.amount === undefined ? left : toCents(body.amount);
    if (!(amount > 0) || amount > left) return [400, { amount: ['Enter an amount up to what is left.'] }];
    const refunded = toCents(payment.refunded_amount || 0) + amount;
    payment.refunded_amount = centsToMoney(refunded);
    payment.status = refunded >= refundableBaseCents(payment) ? 'refunded' : 'partially_refunded';
    return [200, { ...payment, refundable_amount: centsToMoney(refundableCents(payment)) }];
  });

  mock.onGet(EXPORT).reply(async (config) => [200, await paymentsCsv(config.url), {
    'content-disposition': 'attachment; filename="Demo payments.csv"',
  }]);

  mock.onGet(PAYMENTS).reply(async (config) => {
    const data = await eventPayments(config.url, Number(urlParam(config, PAYMENTS)));
    return data ? [200, data] : [404, { detail: 'Not found.' }];
  });

  mock.onPost(PAYMENTS).reply(async (config) => {
    const eventId = Number(urlParam(config, PAYMENTS));
    if (!(await loadEvent(config.url, eventId))) return [404, { detail: 'Not found.' }];
    const body = parseBody(config);
    const errors = validationErrors({ ...body, method: body.method || '' }, { requirePayer: true });
    if (Object.keys(errors).length) return [400, errors];
    const ledger = await loadFixture('events/event-payments');
    const created = {
      id: nextId(ledger.results),
      event: eventId,
      profile_id: body.profile_id ? Number(body.profile_id) : null,
      public_rsvp_id: body.public_rsvp_id ? Number(body.public_rsvp_id) : null,
      amount: money(body.amount),
      method: body.method,
      status: 'paid',
      paid_at: body.paid_at || new Date().toISOString().slice(0, 10),
      note: body.note || '',
      recorded_by_name: `${CURRENT_USER_PROFILE_REF.first_name} ${CURRENT_USER_PROFILE_REF.last_name}`,
    };
    ledger.results.push(created);
    ledger.count += 1;
    return [201, created];
  });

  mock.onPatch(PAYMENT_DETAIL).reply(async (config) => {
    const ledger = await loadFixture('events/event-payments');
    const payment = ledger.results.find((item) => (
      item.id === Number(urlParam(config, PAYMENT_DETAIL, 2)) &&
      item.event === Number(urlParam(config, PAYMENT_DETAIL))
    ));
    if (!payment) return [404, { detail: 'Not found.' }];
    if (payment.method === 'card') return [400, { method: ['Card payments are changed by refunding them.'] }];
    const body = parseBody(config);
    const errors = validationErrors({ ...body, method: body.method || payment.method }, { requirePayer: false });
    if (Object.keys(errors).length) return [400, errors];
    ['method', 'paid_at', 'note'].forEach((key) => {
      if (body[key] !== undefined) payment[key] = body[key];
    });
    if (body.amount !== undefined) payment.amount = money(body.amount);
    return [200, payment];
  });

  mock.onDelete(PAYMENT_DETAIL).reply(async (config) => {
    const ledger = await loadFixture('events/event-payments');
    const payment = ledger.results.find((item) => (
      item.id === Number(urlParam(config, PAYMENT_DETAIL, 2)) &&
      item.event === Number(urlParam(config, PAYMENT_DETAIL))
    ));
    if (!payment) return [404, { detail: 'Not found.' }];
    if (payment.method === 'card') return [400, { method: ['Card payments are changed by refunding them.'] }];
    payment.status = 'voided';
    return [204];
  });
}
